/**
 * The bento's sample standings (`EX-PUB19`): five made-up teams, labelled as a
 * sample on the page, unfreezing the way an ICPC resolver does it. The lowest
 * team with a frozen cell resolves first, a solve can carry a row up past
 * another, and the last resolution flips the lead back.
 *
 * Pure: the scoreboard at any step is computed, never stored, so a test drives
 * it with numbers and the component only chooses which step to draw.
 */

export type SampleCellState = "ac" | "wa" | "fz" | "no";

type SampleCell = { state: SampleCellState; tries: number; minute: number };

const ac = (tries: number, minute: number): SampleCell => ({ state: "ac", tries, minute });
const wa = (tries: number): SampleCell => ({ state: "wa", tries, minute: 0 });
const fz = (tries: number): SampleCell => ({ state: "fz", tries, minute: 0 });
const none = (): SampleCell => ({ state: "no", tries: 0, minute: 0 });

const TEAMS: ReadonlyArray<{ name: string; cells: readonly SampleCell[] }> = [
  { name: "Off By One", cells: [ac(1, 12), ac(2, 48), ac(1, 95), fz(2), none()] },
  { name: "Runtime Terror", cells: [ac(1, 9), ac(1, 61), fz(1), ac(3, 140), none()] },
  { name: "Segment Tree Lovers", cells: [ac(1, 15), ac(1, 70), wa(2), none(), fz(1)] },
  { name: "Null Pointers", cells: [ac(2, 20), wa(1), fz(3), none(), none()] },
  { name: "Big O Notation", cells: [ac(1, 31), none(), none(), none(), fz(1)] },
];

/** Team index, problem index, what the frozen cell turns out to be; bottom-up, as a resolver goes. */
const RESOLUTIONS: ReadonlyArray<readonly [number, number, SampleCell]> = [
  [3, 2, ac(3, 238)],
  [4, 4, wa(1)],
  [2, 4, ac(1, 281)],
  [1, 2, ac(1, 262)],
  [0, 3, ac(2, 270)],
];

export const SAMPLE_PROBLEMS = ["A", "B", "C", "D", "E"] as const;
export const SAMPLE_STEP_MS = 1600;
/** Within a step, the frozen cell is highlighted for this long before it resolves. */
export const SAMPLE_PICK_MS = 700;
/** Step 0 is the frozen board, steps 1 to 5 resolve one cell each, 6 is the final board. */
export const SAMPLE_FINAL_STEP = RESOLUTIONS.length + 1;
const PENALTY_PER_WRONG = 20;

export type SampleCellView = {
  state: SampleCellState;
  /** "+", "+1", "−2", "?" or "". */
  mark: string;
  /** Minute of the solve, or the tries on a frozen cell. */
  detail: string;
  picked: boolean;
  balloon: boolean;
};

export type SampleRowView = {
  name: string;
  rank: number;
  solved: number;
  penalty: number;
  highlighted: boolean;
  cells: SampleCellView[];
};

export type SampleStage = "frozen" | "unfreezing" | "final";

export function sampleStage(step: number): SampleStage {
  if (step <= 0) return "frozen";
  return step >= SAMPLE_FINAL_STEP ? "final" : "unfreezing";
}

/**
 * The board at `step` (0 to {@link SAMPLE_FINAL_STEP}), `sub` milliseconds into
 * it. Rows come back in the teams' own order, each with its rank, so a
 * renderer that keeps the DOM order fixed can move rows by rank alone.
 */
export function sampleStandings(step: number, sub: number): SampleRowView[] {
  const resolving = step >= 1 && step <= RESOLUTIONS.length ? step - 1 : -1;
  const applied =
    step <= 0 ? 0 : step > RESOLUTIONS.length ? RESOLUTIONS.length : step - 1 + (sub >= SAMPLE_PICK_MS ? 1 : 0);

  const cells = TEAMS.map((team) => team.cells.map((cell) => ({ ...cell })));
  for (const [teamIndex, problemIndex, result] of RESOLUTIONS.slice(0, applied)) {
    const row = cells[teamIndex];
    if (row) row[problemIndex] = { ...result };
  }

  const totals = cells.map((row, teamIndex) => {
    let solved = 0;
    let penalty = 0;
    for (const cell of row) {
      if (cell.state !== "ac") continue;
      solved += 1;
      penalty += cell.minute + PENALTY_PER_WRONG * (cell.tries - 1);
    }
    return { teamIndex, solved, penalty };
  });
  const order = [...totals].sort(
    (left, right) => right.solved - left.solved || left.penalty - right.penalty || left.teamIndex - right.teamIndex,
  );
  const rankOf = new Map(order.map((entry, index) => [entry.teamIndex, index + 1]));

  const current = resolving >= 0 ? RESOLUTIONS[resolving] : undefined;
  const balloonShown = current !== undefined && sub >= SAMPLE_PICK_MS && current[2].state === "ac";

  return TEAMS.map((team, teamIndex) => ({
    name: team.name,
    rank: rankOf.get(teamIndex) ?? teamIndex + 1,
    solved: totals[teamIndex]?.solved ?? 0,
    penalty: totals[teamIndex]?.penalty ?? 0,
    highlighted: current !== undefined && current[0] === teamIndex,
    cells: (cells[teamIndex] ?? []).map((cell, problemIndex) => {
      const isCurrent = current !== undefined && current[0] === teamIndex && current[1] === problemIndex;
      return {
        state: cell.state,
        mark:
          cell.state === "ac"
            ? cell.tries > 1
              ? `+${cell.tries - 1}`
              : "+"
            : cell.state === "wa"
              ? `−${cell.tries}`
              : cell.state === "fz"
                ? "?"
                : "",
        detail: cell.state === "ac" ? String(cell.minute) : cell.state === "fz" ? String(cell.tries) : "",
        picked: isCurrent && sub < SAMPLE_PICK_MS,
        balloon: isCurrent && balloonShown,
      };
    }),
  }));
}

/** Where a replay started `elapsedMs` ago stands; `null` elapsed shows the frozen board. */
export function sampleStepAt(elapsedMs: number | null): { step: number; sub: number } {
  if (elapsedMs === null || elapsedMs < 0) return { step: 0, sub: 0 };
  const step = Math.floor(elapsedMs / SAMPLE_STEP_MS);
  if (step >= SAMPLE_FINAL_STEP) return { step: SAMPLE_FINAL_STEP, sub: 0 };
  return { step, sub: elapsedMs % SAMPLE_STEP_MS };
}
